/**
 * Shared display constants and formatters for the Queue Status page.
 *
 * Colors are categorical (identity of a queue), assigned in fixed order so a
 * queue never changes hue as others appear/disappear. Label, order and color
 * live in one table so a new queue cannot be half-registered.
 */
import { slateInkTokens } from '@src/theme/slateInkTheme';

import type { QueueMetrics } from './types';

const QUEUES = {
  // Rust is the theme accent; the other two are chosen for separation from it
  // and from each other under the common forms of color-vision deficiency.
  pricing: { label: 'Pricing Updates', color: slateInkTokens.accent },
  inventory: { label: 'Inventory Updates', color: '#2F6BC4' },
  catalog: { label: 'Catalog Updates', color: '#B03A83' },
} as const;

/** Canonical ordering. Unknown queues sort after these, alphabetically. */
export const QUEUE_ORDER: string[] = Object.keys(QUEUES);

const FALLBACK_QUEUE_COLOR = slateInkTokens.muted;

const queueEntry = (name: string): { label: string, color: string } | undefined =>
  (QUEUES as Record<string, { label: string, color: string }>)[name];

export const queueDisplayName = (name: string): string => queueEntry(name)?.label ?? name;

export const queueColor = (name: string): string =>
  queueEntry(name)?.color ?? FALLBACK_QUEUE_COLOR;

/** Sort queues into canonical order (unknown queues go last, alphabetical). */
export const sortQueues = <T extends { name: string }>(queues: T[]): T[] =>
  [...queues].sort((a, b) => {
    const ai = QUEUE_ORDER.indexOf(a.name);
    const bi = QUEUE_ORDER.indexOf(b.name);
    if (ai === -1 && bi === -1) return a.name.localeCompare(b.name);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

export type QueueHealth = 'clear' | 'draining' | 'waiting' | 'unavailable';

/**
 * Derived only from the SQS counts, which are current. `throughputPerMinute`
 * deliberately measures a window ending 15 minutes ago (reware-api's
 * aws_queue_metrics.ts offsets it to let CloudWatch publish), so it cannot
 * answer "is this queue moving right now" — a queue that stalled two minutes
 * ago would still have read "Draining", and one that recovered five minutes
 * ago would still have read "Stalled".
 *
 * `messagesProcessing` is SQS's ApproximateNumberOfMessagesNotVisible: work
 * consumers are holding right now, which is the live signal that something is
 * being sent to Shopify.
 */
export const queueHealth = (queue: QueueMetrics): QueueHealth => {
  if (queue.error != null) return 'unavailable';
  if (queue.messagesPending <= 0 && queue.messagesProcessing <= 0) return 'clear';
  if (queue.messagesProcessing > 0) return 'draining';
  return 'waiting';
};

export const HEALTH_LABELS: Record<QueueHealth, string> = {
  clear: 'Clear',
  draining: 'Draining',
  waiting: 'Waiting',
  unavailable: 'Unavailable',
};

/** Soft chip styling per health state (icon + label carry meaning, not color alone). */
export const HEALTH_CHIP_STYLES: Record<QueueHealth, { bg: string, fg: string }> = {
  clear: { bg: slateInkTokens.okBg, fg: slateInkTokens.ok },
  draining: { bg: '#E5EDF9', fg: '#28558F' },
  waiting: { bg: slateInkTokens.headerBg, fg: slateInkTokens.muted },
  unavailable: { bg: '#FBEAEA', fg: '#A02525' },
};

/* ---------- formatters ---------- */

export const formatCount = (value: number): string => Math.round(value).toLocaleString();

// Nullish rather than null: the schema marks these fields optional, so an
// absent metric and a reported null both have to render as "no data".
export const formatThroughput = (value: number | null | undefined): string =>
  value == null ? '—' : `${value.toFixed(1)}/min`;

export const formatEstimate = (minutes: number | null | undefined): string => {
  if (minutes == null) return '—';
  // An elapsed estimate is not "nearly done" — the queue outlived its forecast.
  if (minutes < 0) return 'overdue';
  if (minutes === 0) return 'Queue empty';
  if (minutes < 1) return '< 1 min';
  return `~${Math.ceil(minutes)} min`;
};

export const formatClockTime = (epochMs: number): string =>
  new Date(epochMs).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

export const formatClockTimeWithSeconds = (epochMs: number): string =>
  new Date(epochMs).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });

export const formatAgo = (seconds: number): string => {
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${Math.floor(seconds)}s ago`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ago`;
};
